'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Logo } from '@/components/Logo'
import { ArrowRight, Check, Sparkles, Shield, Clock, CreditCard, Loader2, Zap } from 'lucide-react'
import {
  getPlansForCountry,
  getPlansForCountryAsync,
  detectUserCountry,
  getTrialInfo,
  type ApiPlan,
} from '@/lib/pricing'
import {
  getTrialDurationDays,
  isTrialCreditCardRequired,
  isMultiCurrencyEnabled,
  getCurrencyForCountry,
  formatPrice,
  type SupportedCountry,
} from '@/lib/config'

type PlanWithPricing = ReturnType<typeof getPlansForCountry>[number] | (ApiPlan & { displayPricing: any })

export function PricingPageClient() {
  const t = useTranslations()
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [country, setCountry] = useState<SupportedCountry>('OTHER')
  const [plans, setPlans] = useState<PlanWithPricing[]>([])
  const [loading, setLoading] = useState(true)
  const trialDays = getTrialDurationDays()
  const requiresCreditCard = isTrialCreditCardRequired()
  const showMultiCurrency = isMultiCurrencyEnabled()

  useEffect(() => {
    const detectedCountry = detectUserCountry()
    setCountry(detectedCountry)

    // Fetch plans from API
    const loadPlans = async () => {
      try {
        setLoading(true)
        const apiPlans = await getPlansForCountryAsync(detectedCountry)
        setPlans(apiPlans)
      } catch (error) {
        console.error('Error loading plans:', error)
        // Fallback to static plans
        setPlans(getPlansForCountry(detectedCountry))
      } finally {
        setLoading(false)
      }
    }

    loadPlans()
  }, [])

  const getPlanCta = (plan: typeof plans[0]) => {
    const { hasTrial, trialDays: planTrialDays } = getTrialInfo(plan)

    if (plan.id === 'free') {
      return { text: t('pricing.cta.getStarted'), href: '/signup', trial: false }
    }

    if (plan.id === 'business') {
      return { text: t('pricing.cta.contactSales'), href: '/contact', trial: false }
    }

    if (hasTrial) {
      return {
        text: t('pricing.cta.startTrial', { days: planTrialDays || trialDays }),
        href: `/signup?plan=${plan.id}&trial=true`,
        trial: true,
      }
    }

    return { text: t('pricing.cta.subscribe'), href: `/signup?plan=${plan.id}`, trial: false }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/90 backdrop-blur-lg border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/" className="flex items-center">
              <Logo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <Link href="/login">
                <Button variant="ghost">{t('auth.login')}</Button>
              </Link>
              <Link href="/signup">
                <Button className="group">
                  {t('pricing.cta.getStarted')}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-28">
        {/* Hero Section */}
        <section className="text-center mb-20">
          {/* Section Badge */}
          <div className="inline-flex items-center gap-3 rounded-full border border-border bg-card px-5 py-2 mb-8">
            <span className="h-2 w-2 rounded-full bg-speaker-3" />
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
              Pricing
            </span>
          </div>

          <h1 className="font-display text-display-lg mb-6 text-balance">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
            {t('pricing.subtitle')}
          </p>

          {/* Billing Cycle Toggle */}
          <div className="inline-flex items-center gap-1 p-1.5 bg-muted rounded-full">
            <Tabs value={billingCycle} onValueChange={(v: string) => setBillingCycle(v as 'monthly' | 'yearly')}>
              <TabsList className="bg-transparent gap-2">
                <TabsTrigger value="monthly" className="px-8">
                  {t('pricing.monthly')}
                </TabsTrigger>
                <TabsTrigger value="yearly" className="relative px-8">
                  {t('pricing.yearly')}
                  <Badge variant="success" className="absolute -top-2 -right-2 border-0 px-2 py-0.5">
                    -17%
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {showMultiCurrency && country === 'BR' && (
            <p className="text-sm text-muted-foreground mt-6 flex items-center justify-center gap-2">
              <Zap className="h-4 w-4 text-speaker-3-text" />
              {t('pricing.localPricing', { country: 'Brazil' })}
            </p>
          )}
        </section>

        {/* Pricing Cards */}
        {loading ? (
          <section className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </section>
        ) : (
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-28">
            {plans.map((plan) => {
              const cta = getPlanCta(plan)
              const price = billingCycle === 'monthly'
                ? plan.displayPricing.monthly
                : plan.displayPricing.yearly
              // Get features array - handle both API format and legacy format
              const features = 'limits' in plan && plan.limits
                ? [
                    `${plan.limits.audioMinutesMonthly > 0 ? plan.limits.audioMinutesMonthly.toLocaleString() + ' audio minutes' : 'Unlimited audio'}`,
                    ...(plan.limits.documentsMonthly > 0 ? [`${plan.limits.documentsMonthly} documents/month`] : []),
                    ...(Array.isArray(plan.features) ? plan.features.slice(0, 5) : []),
                  ]
                : plan.features

              const isHighlighted = plan.highlighted

              return (
                <div key={plan.id} className="relative">
                  {isHighlighted ? (
                    // Chroma "Notiia summary"-style card: ink-filled, amber label
                    <Card className="relative flex flex-col h-full bg-primary text-primary-foreground border-0 rounded-2xl shadow-float">
                      {/* Popular label, amber like the summary card eyebrow */}
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-speaker-3 text-primary px-5 py-1.5 rounded-full text-sm font-bold shadow-sm">
                        {t('pricing.mostPopular')}
                      </div>

                      <CardHeader>
                        <CardTitle className="text-2xl text-primary-foreground">{plan.name}</CardTitle>
                        <CardDescription className="text-primary-foreground/70">{plan.description}</CardDescription>
                      </CardHeader>

                      <CardContent className="flex-1 flex flex-col">
                        <div className="mb-8">
                          <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-display text-primary-foreground">{price}</span>
                            <span className="text-primary-foreground/60 text-sm">
                              /{billingCycle === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}
                            </span>
                          </div>

                          {billingCycle === 'yearly' && plan.displayPricing.yearlySavings > 0 && (
                            <Badge className="mt-3 border-0 bg-speaker-3/20 text-speaker-3 rounded-full px-3">
                              {t('pricing.save', { percent: plan.displayPricing.yearlySavings })}
                            </Badge>
                          )}
                        </div>

                        {/* Trial Info */}
                        {cta.trial && (
                          <div className="flex items-center gap-2 p-4 bg-primary-foreground/10 rounded-xl text-sm mb-6">
                            <Sparkles className="w-5 h-5 text-speaker-3 flex-shrink-0" />
                            <span className="text-primary-foreground font-medium">
                              {requiresCreditCard
                                ? t('pricing.trialWithCard', { days: ('trialDays' in plan ? plan.trialDays : trialDays) || trialDays })
                                : t('pricing.trialNoCard', { days: ('trialDays' in plan ? plan.trialDays : trialDays) || trialDays })}
                            </span>
                          </div>
                        )}

                        <div className="space-y-4 flex-1 mb-8">
                          {(Array.isArray(features) ? features : []).map((feature, index) => (
                            <div key={index} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-speaker-2 flex-shrink-0 mt-0.5" />
                              <span className="text-primary-foreground/90 text-sm leading-relaxed">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <Link href={cta.href} className="w-full block mt-auto">
                          <Button className="w-full gap-2 group bg-card text-primary hover:bg-card/90" size="lg">
                            {cta.text}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="relative flex flex-col h-full rounded-2xl transition-all duration-300 hover:border-foreground/20">
                      <CardHeader>
                        <CardTitle className="text-2xl">{plan.name}</CardTitle>
                        <CardDescription className="text-muted-foreground">{plan.description}</CardDescription>
                      </CardHeader>

                      <CardContent className="flex-1 flex flex-col">
                        <div className="mb-8">
                          <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-display">{price}</span>
                            <span className="text-muted-foreground text-sm">
                              /{billingCycle === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}
                            </span>
                          </div>

                          {billingCycle === 'yearly' && plan.displayPricing.yearlySavings > 0 && (
                            <Badge variant="success" className="mt-3 rounded-full px-3">
                              {t('pricing.save', { percent: plan.displayPricing.yearlySavings })}
                            </Badge>
                          )}
                        </div>

                        {/* Trial Info */}
                        {cta.trial && (
                          <div className="flex items-center gap-2 p-4 bg-muted rounded-xl text-sm mb-6">
                            <Sparkles className="w-5 h-5 text-speaker-3-text flex-shrink-0" />
                            <span className="text-foreground font-medium">
                              {requiresCreditCard
                                ? t('pricing.trialWithCard', { days: ('trialDays' in plan ? plan.trialDays : trialDays) || trialDays })
                                : t('pricing.trialNoCard', { days: ('trialDays' in plan ? plan.trialDays : trialDays) || trialDays })}
                            </span>
                          </div>
                        )}

                        <div className="space-y-4 flex-1 mb-8">
                          {(Array.isArray(features) ? features : []).map((feature, index) => (
                            <div key={index} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-speaker-2 flex-shrink-0 mt-0.5" />
                              <span className="text-foreground text-sm leading-relaxed">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <Link href={cta.href} className="w-full block mt-auto">
                          <Button
                            className="w-full gap-2 group"
                            variant={plan.id === 'free' ? 'outline' : 'default'}
                            size="lg"
                          >
                            {cta.text}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )
            })}
          </section>
        )}

        {/* Trust Badges */}
        <section className="flex flex-wrap justify-center gap-12 mb-28 text-sm">
          <div className="flex items-center gap-3 group">
            <div className="p-3 rounded-xl bg-card border border-border group-hover:border-foreground/20 transition-colors">
              <Shield className="w-5 h-5 text-speaker-2" />
            </div>
            <span className="text-muted-foreground font-medium">{t('pricing.badges.secure')}</span>
          </div>
          <div className="flex items-center gap-3 group">
            <div className="p-3 rounded-xl bg-card border border-border group-hover:border-foreground/20 transition-colors">
              <Clock className="w-5 h-5 text-speaker-5" />
            </div>
            <span className="text-muted-foreground font-medium">{t('pricing.badges.cancelAnytime')}</span>
          </div>
          <div className="flex items-center gap-3 group">
            <div className="p-3 rounded-xl bg-card border border-border group-hover:border-foreground/20 transition-colors">
              <CreditCard className="w-5 h-5 text-speaker-1" />
            </div>
            <span className="text-muted-foreground font-medium">{t('pricing.badges.moneyBack')}</span>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-card border border-border rounded-2xl p-12 mb-28">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 rounded-full border border-border bg-background px-5 py-2 mb-8">
              <span className="h-2 w-2 rounded-full bg-speaker-1" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                FAQ
              </span>
            </div>

            <h2 className="font-display text-display mb-4">
              {t('pricing.faq.title')}
            </h2>
          </div>

          <div className="space-y-8 max-w-3xl mx-auto">
            <div className="p-6 rounded-xl border border-border bg-background hover:border-foreground/20 transition-colors">
              <h3 className="text-lg font-semibold mb-3">
                {t('pricing.faq.changePlan.question')}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t('pricing.faq.changePlan.answer')}
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-background hover:border-foreground/20 transition-colors">
              <h3 className="text-lg font-semibold mb-3">
                {t('pricing.faq.refund.question')}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t('pricing.faq.refund.answer')}
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-background hover:border-foreground/20 transition-colors">
              <h3 className="text-lg font-semibold mb-3">
                {t('pricing.faq.payment.question')}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t('pricing.faq.payment.answer')}
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-background hover:border-foreground/20 transition-colors">
              <h3 className="text-lg font-semibold mb-3">
                {t('pricing.faq.trial.question')}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t('pricing.faq.trial.answer', { days: trialDays })}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section - Inverted */}
        <section className="bg-primary text-primary-foreground rounded-3xl p-16 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-display text-display mb-6 text-primary-foreground text-balance">
              {t('pricing.cta.ready')}
            </h2>
            <p className="text-lg mb-4 text-primary-foreground/80 leading-relaxed max-w-2xl mx-auto">
              {t('pricing.cta.subtitle')}
            </p>
            <p className="text-sm mb-10 text-primary-foreground/60">
              {requiresCreditCard
                ? t('pricing.cta.noChargeUntilTrial')
                : t('pricing.cta.noCreditCard')}
            </p>
            <Link href="/signup?plan=pro&trial=true">
              <Button size="lg" className="gap-2 group text-lg h-16 px-10 bg-card text-primary hover:bg-card/90">
                {t('pricing.cta.startTrial', { days: trialDays })}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}
